import React from 'react';
import { cn } from '@/lib/utils';
import { FramedAvatar } from './FramedAvatar';

interface PartnerDisplayProps {
  partner: {
    id: string;
    partner_name: string;
    partner_username?: string;
    partner_avatar_url?: string;
    partner_belt_rank?: string;
    is_manual: boolean;
  };
  className?: string;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export function PartnerDisplay({ 
  partner, 
  className = "",
  showRemoveButton = false,
  onRemove,
  size = 'md'
}: PartnerDisplayProps) {
  const initials = partner.partner_name
    ? partner.partner_name
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'UN';

  // Size configurations
  const sizeConfig = {
    sm: { avatarSize: 24, frameSize: { w: 32, h: 36 }, fontSize: 10 },
    md: { avatarSize: 32, frameSize: { w: 40, h: 44 }, fontSize: 12 },
    lg: { avatarSize: 42, frameSize: { w: 60, h: 66 }, fontSize: 16 }
  };

  const config = sizeConfig[size];

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <FramedAvatar
        src={partner.partner_avatar_url}
        alt={partner.partner_name || 'Partner'}
        initials={initials}
        beltRank={partner.partner_belt_rank}
        size={config.avatarSize}
        frameSize={config.frameSize}
        fontSize={config.fontSize}
      />
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 truncate">
          {partner.partner_name || 'Unknown Partner'}
        </span>
        {partner.partner_username && (
          <span className="text-xs text-gray-500 truncate">
            @{partner.partner_username}
          </span>
        )}
      </div>
      {showRemoveButton && onRemove && (
        <button
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 p-1"
          title="Remove partner"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
} 